import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Skeleton } from '@mui/material';
import { Formik, Field, FormikErrors } from 'formik';
import { buildNavItems, useApi, useAuth, useCerebroNav } from '../shared/context';
import {
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroSelectableIcon,
	CerebroDrawer,
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroIcon,
	CerebroDatePicker,
	CerebroForm,
	CerebroCheckbox,
} from '../shared/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';
import { faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as EquipmentFinancing } from '../shared/icons/EquipmentFinancing.svg';
import { ReactComponent as GrowthCapital } from '../shared/icons/GrowthCapital.svg';
import { ReactComponent as Refinance } from '../shared/icons/Refinance.svg';
import { ReactComponent as NewBusiness } from '../shared/icons/NewBusiness.svg';
import { ReactComponent as OtherFinancingNeeds } from '../shared/icons/OtherFinancingNeeds.svg';
import { ReactComponent as OwnerOccupiedRealEstate } from '../shared/icons/OwnerOccupiedRealEstate.svg';
import { ReactComponent as WorkingCapital } from '../shared/icons/WorkingCapital.svg';
import { getApiResponseData } from '../shared/utils/axios';
import { useSnackbar } from 'notistack';
import { DealRequestAllocation } from '../shared/models/DealRequestAllocation';
import { DateUtils, FormikUtils } from '../shared/utils';
import { QueryKeys, useDealQuery, useDealRequestAllocationQuery } from '../shared/hooks';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { DealPreferenceDragAndDrop, DealPreferenceItem } from '../shared/components/DealPreferenceDragAndDrop';
import { Deal, UpdateDealDto } from '../shared/models';
import { AxiosInstance } from 'axios';
import { validateDateWithinPlusMinus100Years } from '../shared/utils/date';

export const DealIntroduction: React.FC = () => {
	const [open, setOpen] = React.useState(false);
	const [items, setItems] = React.useState<DealPreferenceItem[]>([]);

	const api = useApi();
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();
	const navigate = useNavigate();
	const nav = useCerebroNav();

	const dealQuery = useDealQuery(guid);
	const allocationQuery = useDealRequestAllocationQuery(guid);

	const client = useQueryClient();
	const snackbar = useSnackbar();

	React.useEffect(() => {
		setItems(getDealPreferenceItems(dealQuery.data));
	}, [setItems, dealQuery.data]);

	const introMutation = useMutation(
		async ({ values }: DealIntroductionMutationVariables) => {
			const dealPatch: Partial<UpdateDealDto> = {
				targetFundingDate: values.targetFundingDate?.toISOString().split('T')[0],
				isAsapTargetDate: values.isAsapTargetDate,
				pricePriority: getPriorityTextFromValue(items.findIndex(i => i.propertyName == 'PricePriority')),
				maturityPriority: getPriorityTextFromValue(items.findIndex(i => i.propertyName == 'MaturityPriority')),
				covenantPriority: getPriorityTextFromValue(items.findIndex(i => i.propertyName == 'CovenantPriority')),
				amountPriority: getPriorityTextFromValue(items.findIndex(i => i.propertyName == 'AmountPriority')),
				amortizationPriority: getPriorityTextFromValue(
					items.findIndex(i => i.propertyName == 'AmortizationPriority')
				),
				sourcePage: 'Introduction',
			};

			const deal = await getAndUpdateDeal(api, guid, dealPatch);

			let allocation: DealRequestAllocation | null = null;
			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), {
					...values,
					hasMachineryEquipment: values.purchaseEquipment,
					hasOwnerOccupiedRealEstate: values.purchaseRealEstate,
					hasCashMarketables: values.financeExpensesOrGrowth || values.purchaseWorkingCapital,
					hasAccountsReceivables: values.financeExpensesOrGrowth || values.purchaseWorkingCapital,
					hasInventory: values.financeExpensesOrGrowth || values.purchaseWorkingCapital,
					debtCapitalForBusiness:
						values.financeExpensesOrGrowth ||
						values.otherFinancingNeeds ||
						values.purchaseEquipment ||
						values.purchaseRealEstate ||
						values.purchaseWorkingCapital ||
						values.refinanceExistingDebt,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			allocation = data;

			if (allocation == null) {
				throw new Error('Allocation not found');
			}

			return { deal, allocation };
		},
		{
			onMutate: ({ values }) => {
				client.setQueryData(QueryKeys.Allocation(guid), {
					...allocationQuery.data,
					...values,
					hasMachineryEquipment: values.purchaseEquipment,
					hasOwnerOccupiedRealEstate: values.purchaseRealEstate,
					hasCashMarketables: values.financeExpensesOrGrowth || values.purchaseWorkingCapital,
					hasAccountsReceivables: values.financeExpensesOrGrowth || values.purchaseWorkingCapital,
					hasInventory: values.financeExpensesOrGrowth || values.purchaseWorkingCapital,
					debtCapitalForBusiness:
						values.financeExpensesOrGrowth ||
						values.otherFinancingNeeds ||
						values.purchaseEquipment ||
						values.purchaseRealEstate ||
						values.purchaseWorkingCapital ||
						values.refinanceExistingDebt,
				});
			},
			onSuccess: data => {
				const navState = buildNavItems(
					currentUser,
					data.allocation,
					data.deal,
					location.pathname,
					client.getQueryData(QueryKeys.Questions.LoanApp),
					client.getQueryData(QueryKeys.Product(guid)),
					client.getQueryData(QueryKeys.RequiredDocuments.Root(guid)),
					client.getQueryData(QueryKeys.Integrations.Config())
				);
				nav.setState(navState);

				client.setQueryData(QueryKeys.Allocation(guid), data.allocation);

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				if (navState.nextNavItem !== undefined) {
					navigate(`/${guid}/${navState.nextNavItem.url}`);
				}
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Deal &gt; Financing Needs</CerebroBreadcrumb>

			<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
				Let&apos;s find you a loan!
			</CerebroHeading>

			<CerebroText sx={{ marginBottom: 8 }}>
				Tell us about your financing needs and the type financing you are requesting. You may select more than
				one.
			</CerebroText>

			{allocationQuery.isLoading ? (
				<Skeleton height={400} />
			) : (
				<Formik
					initialValues={{
						acquiringBusiness: allocationQuery.data?.acquiringBusiness ?? false,
						refinanceExistingDebt: allocationQuery.data?.refinanceExistingDebt ?? false,
						purchaseEquipment: allocationQuery.data?.purchaseEquipment ?? false,
						purchaseRealEstate: allocationQuery.data?.purchaseRealEstate ?? false,
						purchaseWorkingCapital: allocationQuery.data?.purchaseWorkingCapital ?? false,
						financeExpensesOrGrowth: allocationQuery.data?.financeExpensesOrGrowth ?? false,
						otherFinancingNeeds: allocationQuery.data?.otherFinancingNeeds ?? false,
						targetFundingDate: DateUtils.ParseDateOnly(dealQuery.data?.targetFundingDate),
						isAsapTargetDate: dealQuery.data?.isAsapTargetDate ?? false,
					}}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(introMutation.mutate)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
						const error = errors.acquiringBusiness !== undefined && touched.acquiringBusiness;

						return (
							<CerebroForm onSubmit={handleSubmit}>
								<CerebroLabel
									required
									icon={() => <FontAwesomeIcon icon={faInfoCircle} />}
									onIconClick={() => setOpen(true)}
									error={error}
								>
									What are your financing needs (select all that apply)?
								</CerebroLabel>

								<br />

								<Grid container spacing={2} sx={{ marginBottom: 4 }}>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="purchaseWorkingCapital"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <WorkingCapital />}
										>
											Working Capital
										</Field>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="financeExpensesOrGrowth"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <GrowthCapital />}
										>
											Growth Capital
										</Field>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="purchaseEquipment"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <EquipmentFinancing />}
										>
											Equipment Financing
										</Field>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="purchaseRealEstate"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <OwnerOccupiedRealEstate />}
										>
											Owner-Occupied Real Estate
										</Field>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="refinanceExistingDebt"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <Refinance width="52" />}
										>
											Refinance Existing Debt
										</Field>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="acquiringBusiness"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <NewBusiness />}
										>
											Acquiring a Business
										</Field>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<Field
											as={CerebroSelectableIcon}
											name="otherFinancingNeeds"
											sx={{ marginBottom: 2 }}
											error={error}
											stretch
											disabled={isSubmitting}
											icon={() => <OtherFinancingNeeds />}
										>
											Other Financing Needs
										</Field>
									</Grid>
								</Grid>

								{errors.acquiringBusiness && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										You must select at least one financing need.
									</CerebroText>
								)}

								{currentUser && currentUser.isEmployeeUser && (
									<>
										<Grid container spacing={3} sx={{ marginBottom: 4 }}>
											<Grid item>
												<CerebroLabel gutterBottom>What is most important to you?</CerebroLabel>
												<CerebroText gutterBottom variant="small">
													Help us understand what matters most to you so we can match you the
													best loan options based on your priorities. Drag and drop to arrange
													so that your most important priority is at the top.
												</CerebroText>
											</Grid>
											<Grid item xs={12} md={6}>
												<DealPreferenceDragAndDrop
													skeleton={isSubmitting}
													items={items}
													setItems={setItems}
												/>
											</Grid>
										</Grid>

										<Grid container spacing={3} sx={{ marginBottom: 4 }}>
											<Grid item xs={6}>
												<CerebroLabel htmlFor="targetFundingDate" gutterBottom>
													Target Funding Date{' '}
												</CerebroLabel>
												<Field
													id="targetFundingDate"
													name="targetFundingDate"
													component={CerebroDatePicker}
													disabled={isSubmitting || values.isAsapTargetDate}
													error={
														touched.targetFundingDate &&
														errors.targetFundingDate !== undefined
													}
												/>
												{touched.targetFundingDate && errors.targetFundingDate && (
													<CerebroText variant="error" sx={{ marginBottom: 4 }}>
														{errors.targetFundingDate}
													</CerebroText>
												)}
											</Grid>
											<Grid item xs={6}>
												<CerebroLabel
													htmlFor="isAsapTargetDate"
													gutterBottom
													sx={{ marginTop: 4 }}
												>
													<Field
														id="isAsapTargetDate"
														name="isAsapTargetDate"
														as={CerebroCheckbox}
														disabled={isSubmitting}
														error={
															touched.isAsapTargetDate &&
															errors.isAsapTargetDate !== undefined
														}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
															setFieldValue('isAsapTargetDate', e.target.checked);
															if (e.target.checked) {
																values.targetFundingDate = null;
																setFieldValue('targetFundingDate', null);
															}
														}}
													/>
													ASAP
												</CerebroLabel>
											</Grid>
										</Grid>
									</>
								)}

								<Grid
									container
									sx={{ paddingBottom: 2 }}
									justifyContent={{ xs: 'center', md: 'flex-start' }}
								>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Save &amp; Continue
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			)}

			<CerebroDrawer open={open} onClose={() => setOpen(false)}>
				<CerebroIcon sx={{ marginBottom: 2 }}>
					<FontAwesomeIcon icon={faInfoCircle} size="3x" />
				</CerebroIcon>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					What are your financing needs?
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Select the type financing you need. You can pick more than one if you have multiple reasons for
					seeking a loan.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Working Capital
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Loan used to fund day-to-day business operations.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Growth Capital
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Funds used to grow or expand your existing business.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Equipment Financing
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Asset-backed loan used for the purchase, repair, or replacement of equipment.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Owner-Occupied Real Estate
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Mortgage that&apos;s secured by commercial property such as industrial, retail, or office buildings.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Refinancing Existing Debt
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Any existing loans that need to be refinanced with this request.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Acquiring a Business
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Financing used to purchase or buy-out a business.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Other Financing Needs
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Any other need that isn&apos;t already selected.
				</CerebroText>

				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					Need help? Speak to a Cerebro expert.
				</CerebroText>

				<FontAwesomeIcon icon={faPhoneAlt} size="1x" />
				<CerebroText variant="small" component="span">
					(240) 394-1311
				</CerebroText>
			</CerebroDrawer>
		</CerebroPageContainer>
	);
};

type DealIntroductionMutationVariables = FormikUtils.MutateOnSubmitVariables<DealIntroductionFormValues>;
interface DealIntroductionFormValues {
	acquiringBusiness: boolean;
	refinanceExistingDebt: boolean;
	purchaseEquipment: boolean;
	purchaseRealEstate: boolean;
	purchaseWorkingCapital: boolean;
	financeExpensesOrGrowth: boolean;
	otherFinancingNeeds: boolean;
	targetFundingDate: Date | null;
	isAsapTargetDate: boolean;
}

type DealIntroductionFormValueErrors = FormikErrors<DealIntroductionFormValues>;

function getPriorityTextFromValue(value?: number | null) {
	switch (value) {
		case 0:
			return 'Very Important';
		case 1:
			return 'Important';
		case 2:
			return 'Neutral';
		case 3:
			return 'Not Important';
		case 4:
			return 'Marginal';
		default:
			return 'NA';
	}
}

function getPriorityValueFromText(text: string) {
	switch (text) {
		case 'Very Important':
			return 0;
		case 'Important':
			return 1;
		case 'Neutral':
			return 2;
		case 'Not Important':
			return 3;
		case 'Marginal':
			return 4;
		default:
			return 5;
	}
}

function getDealPreferenceItems(value?: Deal) {
	if (value === undefined) return [];
	const items: any[] = [
		{
			displayName: 'Cost of Capital',
			propertyName: 'PricePriority',
			index: getPriorityValueFromText(value.pricePriority),
		},
		{
			displayName: 'Covenant Levels',
			propertyName: 'CovenantPriority',
			index: getPriorityValueFromText(value.covenantPriority),
		},
		{
			displayName: 'Term of Loan',
			propertyName: 'MaturityPriority',
			index: getPriorityValueFromText(value.maturityPriority),
		},
		{
			displayName: 'Amortization',
			propertyName: 'AmortizationPriority',
			index: getPriorityValueFromText(value.amortizationPriority),
		},
		{
			displayName: 'Size of Loan',
			propertyName: 'AmountPriority',
			index: getPriorityValueFromText(value.amountPriority),
		},
	];

	return items
		.sort((a, b) => a.index - b.index)
		.map<DealPreferenceItem>((item, index) => {
			return { propertyName: item.propertyName, displayName: item.displayName, index: index + 1 };
		});
}

function validateForm(values: DealIntroductionFormValues) {
	const errors: DealIntroductionFormValueErrors = {};

	if (
		!values.acquiringBusiness &&
		!values.financeExpensesOrGrowth &&
		!values.otherFinancingNeeds &&
		!values.purchaseEquipment &&
		!values.purchaseRealEstate &&
		!values.purchaseWorkingCapital &&
		!values.refinanceExistingDebt
	) {
		errors.acquiringBusiness = 'You must select at least one financing need.';
	}

	if (!values.isAsapTargetDate) {
		const dateError = validateDateWithinPlusMinus100Years(values.targetFundingDate, 'Target Funding Date');
		if (dateError) {
			console.log(dateError)
			errors.targetFundingDate = dateError;
		}
	}

	return errors;
}

export async function getAndUpdateDeal(
	api: AxiosInstance,
	guid: string | undefined,
	dealPatch: UpdateDealDto
): Promise<Deal> {
	const { data, error } = await getApiResponseData<Deal>(api.patch(CerebroApiEndpoints.Deals.Root(guid), dealPatch));

	if (error) {
		throw new Error(error.response?.data);
	}
	if (data == null) {
		throw new Error('Deal not found');
	}

	return data;
}
